import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_workbox_9aa73f5e from 'nuxt_plugin_workbox_9aa73f5e' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_nuxticons_3feb75f3 from 'nuxt_plugin_nuxticons_3feb75f3' // Source: ./nuxt-icons.js (mode: 'all')
import nuxt_plugin_axios_49d73475 from 'nuxt_plugin_axios_49d73475' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_importGlobalComponents_4f10ee36 from 'nuxt_plugin_importGlobalComponents_4f10ee36' // Source: ../plugins/importGlobalComponents.js (mode: 'all')
import nuxt_plugin_veeValidate_810cbac2 from 'nuxt_plugin_veeValidate_810cbac2' // Source: ../plugins/veeValidate.js (mode: 'all')
import nuxt_plugin_DatePicker_32ddf907 from 'nuxt_plugin_DatePicker_32ddf907' // Source: ../plugins/DatePicker.js (mode: 'all')
import nuxt_plugin_Konva_78ef1436 from 'nuxt_plugin_Konva_78ef1436' // Source: ../plugins/Konva.js (mode: 'all')
import nuxt_plugin_config_e34361f4 from 'nuxt_plugin_config_e34361f4' // Source: ../database/config.js (mode: 'all')
import nuxt_plugin_consolelog_11de0254 from 'nuxt_plugin_consolelog_11de0254' // Source: ../plugins/consolelog.js (mode: 'all')
import nuxt_plugin_toasted_578b7425 from 'nuxt_plugin_toasted_578b7425' // Source: ../plugins/toasted.js (mode: 'all')
import nuxt_plugin_clickOutside_0aede166 from 'nuxt_plugin_clickOutside_0aede166' // Source: ../plugins/clickOutside.js (mode: 'all')
import nuxt_plugin_flareBugTracker_644f5dce from 'nuxt_plugin_flareBugTracker_644f5dce' // Source: ../plugins/flareBugTracker.js (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn(`<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead`)
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>`
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp (ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [ defaultTransition ],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [ transitions ]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        const nuxt = this.nuxt || this.$options.nuxt
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error('inject(key, value) has no value provided')
    }

    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Vue.prototype.hasOwnProperty(key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (process.client && typeof nuxt_plugin_workbox_9aa73f5e === 'function') {
    await nuxt_plugin_workbox_9aa73f5e(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxticons_3feb75f3 === 'function') {
    await nuxt_plugin_nuxticons_3feb75f3(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_49d73475 === 'function') {
    await nuxt_plugin_axios_49d73475(app.context, inject)
  }

  if (typeof nuxt_plugin_importGlobalComponents_4f10ee36 === 'function') {
    await nuxt_plugin_importGlobalComponents_4f10ee36(app.context, inject)
  }

  if (typeof nuxt_plugin_veeValidate_810cbac2 === 'function') {
    await nuxt_plugin_veeValidate_810cbac2(app.context, inject)
  }

  if (typeof nuxt_plugin_DatePicker_32ddf907 === 'function') {
    await nuxt_plugin_DatePicker_32ddf907(app.context, inject)
  }

  if (typeof nuxt_plugin_Konva_78ef1436 === 'function') {
    await nuxt_plugin_Konva_78ef1436(app.context, inject)
  }

  if (typeof nuxt_plugin_config_e34361f4 === 'function') {
    await nuxt_plugin_config_e34361f4(app.context, inject)
  }

  if (typeof nuxt_plugin_consolelog_11de0254 === 'function') {
    await nuxt_plugin_consolelog_11de0254(app.context, inject)
  }

  if (typeof nuxt_plugin_toasted_578b7425 === 'function') {
    await nuxt_plugin_toasted_578b7425(app.context, inject)
  }

  if (typeof nuxt_plugin_clickOutside_0aede166 === 'function') {
    await nuxt_plugin_clickOutside_0aede166(app.context, inject)
  }

  if (typeof nuxt_plugin_flareBugTracker_644f5dce === 'function') {
    await nuxt_plugin_flareBugTracker_644f5dce(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
